/**
 * การตามสอบ Lot (Lot Genealogy) — รูปร่างข้อมูลตาม payload contract ที่ freeze แล้ว
 * (`docs/API_CONTRACT.md` — "Lot Genealogy — Payload Contract", D-073)
 *
 * ไฟล์นี้เป็นภาพสะท้อนของเอกสารนั้นเท่านั้น ไม่ใช่ที่ตัดสินใจเพิ่มเติมเอง สองเส้นทาง
 * (`backward`/`forward`) คืน **รูปเดียวกันทุกอย่าง** ต่างกันแค่ความหมายของ
 * `edges[].lot_id` จึงมีชนิดข้อมูลชุดเดียว ไม่ใช่สองชุดที่ต้องประคองให้ตรงกัน
 *
 * ⚠️ `qty_consumed` เป็น **string** `NUMERIC(18,4)` ที่ผ่าน IEEE-754 double คือ
 * จำนวนที่เพี้ยนไปเงียบ ๆ และปริมาณที่เพี้ยนบนหน้าจอเรียกคืนสินค้าคือจำนวนของที่
 * ตามกลับมาไม่ครบ
 */

/**
 * รหัสโมดูลที่ endpoint ทั้งสองเส้นถูก gate ด้วย — `INVENTORY` ไม่ใช่โมดูลของตัวเอง
 *
 * D-073 ระบุตรง ๆ ว่า Phase 1 **ไม่มีโมดูล `LOT_TRACEABILITY`** ให้ใครถือได้ และ
 * ห้ามสร้างขึ้นใหม่ — route ที่ gate ด้วยโมดูลที่ไม่มีใครถือได้ คือ route ที่ไม่มีใคร
 * เปิดได้ ค่านี้จึงเท่ากับที่ `router.py` ของฝั่งเซิร์ฟเวอร์ใช้จริง
 */
export const INVENTORY_MODULE = 'INVENTORY';

/* ------------------------------------------------------------- direction -- */

/** `GET /v1/lots/{lot_id}/genealogy/backward` — lot ต้นทางที่ถูกใช้มาเป็น lot นี้ */
export const GENEALOGY_BACKWARD = 'backward';

/** `GET /v1/lots/{lot_id}/genealogy/forward` — lot ปลายทางที่ผลิตจาก lot นี้ */
export const GENEALOGY_FORWARD = 'forward';

export const GENEALOGY_DIRECTIONS = [GENEALOGY_BACKWARD, GENEALOGY_FORWARD] as const;

export type GenealogyDirection = (typeof GENEALOGY_DIRECTIONS)[number];

/* -------------------------------------------------------------- resource -- */

/**
 * lot ที่ถูกถาม — คีย์ `lot` ของ payload
 *
 * มีทั้ง id และ code ของสินค้าตามกติกาข้ามกลุ่ม (หน้าจอแสดง code ส่วนลิงก์ใช้ id)
 * และ **ไม่มี `plant_id`/`plant_code`** โดยเจตนาตาม contract — lot ที่ตอบกลับมาได้
 * แปลว่าอยู่ในขอบเขตของผู้เรียกอยู่แล้ว (D-004) ส่วน lot นอกขอบเขตตอบ `404`
 */
export interface LotGenealogyLot {
  readonly lot_id: string;
  readonly product_id: string;
  readonly product_code: string;
  /** **เซิร์ฟเวอร์เป็นเจ้าของ** — หน้าจอนี้อ่านอย่างเดียว ไม่มีเส้นทางเขียน */
  readonly quality_status: string;
}

/**
 * หนึ่ง edge ของ `lot_genealogy` — lot ที่เกี่ยวข้อง และปริมาณที่มันรับผิดชอบ
 *
 * `lot_id` คือ lot **ที่เกี่ยวข้อง** และความหมายขึ้นกับทิศทาง: parent ที่ถูกใช้ไป
 * เมื่อถาม `backward` และ child ที่ถูกผลิตขึ้นเมื่อถาม `forward`
 *
 * หลาย edge = หลายแถว: `backward` ที่คืนหลาย parent คือการ merge ส่วน `forward`
 * ที่คืนหลาย child คือการ split — ทั้งสองเป็นคำตอบปกติ ไม่ใช่ความผิดปกติ
 */
export interface LotGenealogyEdge {
  readonly lot_id: string;
  readonly product_id: string;
  readonly product_code: string;
  readonly quality_status: string;
  /** `NUMERIC(18,4)` — string เสมอ ห้ามแปลงเป็น number ไม่ว่าจะเพื่ออะไร */
  readonly qty_consumed: string;
  /** หน่วยที่ถูกบันทึกไว้ **ราย edge** ไม่ใช่หน่วยฐานของสินค้า */
  readonly uom: string;
  /** cycle การผลิตที่สร้างความสัมพันธ์นี้ — edge เขียนโดย Scan-Out เท่านั้น (D-065) */
  readonly operation_confirmation_id: string;
}

/**
 * `{ lot, edges, next_cursor }` — คำตอบของทั้งสองทิศทาง
 *
 * **`edges` ว่างคือ `200` ไม่ใช่ error** lot ที่มาจากการรับเข้าไม่มี parent และ
 * วัตถุดิบที่ยังไม่ถูกเบิกไปใช้ไม่มี child ทั้งคู่เป็นคำตอบที่ถูกต้องเกี่ยวกับ lot ที่มี
 * อยู่จริง (contract เขียนไว้ว่า "เป็น `[]` ไม่ใช่ error")
 *
 * `edges` เป็น edge **ตรง** หนึ่ง hop เท่านั้น — Phase 1 เป็น single-level การไล่
 * ต่อทำโดยถามใหม่ด้วย lot ที่ได้มา ไม่ใช่ด้วยการกางทั้งต้นไม้ (multi-level เป็น
 * Phase 2) นี่คือเหตุผลที่ไม่มีอะไรใน payload นี้ซ้อนกันเป็นชั้น
 */
export interface LotGenealogy {
  readonly lot: LotGenealogyLot;
  readonly edges: readonly LotGenealogyEdge[];
  readonly next_cursor: string | null;
}

/** พารามิเตอร์ของทั้งสองเส้น — หน้าถัดไปเท่านั้น ไม่มีตัวกรองอื่น */
export interface LotGenealogyQuery {
  readonly limit?: number;
  readonly cursor?: string;
}
